import { useEffect } from "react";

type FeaturebaseBootOptions = {
  appId: string;
  email?: string;
  userId?: string;
  theme?: "light" | "dark";
  language?: string;
};

declare global {
  interface Window {
    Featurebase?: any;
    __featurebaseLoaded?: boolean;
    __featurebaseBooted?: boolean;
    __featurebaseBootedEmail?: string;
    __featurebaseBootedUserId?: string;
    __featurebaseBootedTheme?: string;
  }
}

export function useFeaturebase(options: FeaturebaseBootOptions) {
  const { appId, email, userId, theme = "light", language = "en" } = options;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!appId || !email || !userId) {
      return;
    }

    const win = window as Window & typeof globalThis;

    const ensureSdkLoaded = () =>
      new Promise<void>(resolve => {
        if (win.Featurebase && win.__featurebaseLoaded) {
          resolve();
          return;
        }
        const existing = document.getElementById("featurebase-sdk");
        if (existing) {
          existing.addEventListener("load", () => {
            win.__featurebaseLoaded = true;
            resolve();
          });
          return;
        }
        const script = document.createElement("script");
        script.id = "featurebase-sdk";
        script.src = "https://do.featurebase.app/js/sdk.js";
        script.async = true;
        script.onload = () => {
          win.__featurebaseLoaded = true;
          resolve();
        };
        const firstScript = document.getElementsByTagName("script")[0];
        firstScript.parentNode!.insertBefore(script, firstScript);
      });

    ensureSdkLoaded().then(() => {
      if (!win.Featurebase) {
        win.Featurebase = function () {
          (win.Featurebase.q = win.Featurebase.q || []).push(arguments);
        };
      }
      if (
        win.__featurebaseBooted &&
        win.__featurebaseBootedEmail === email &&
        win.__featurebaseBootedUserId === userId &&
        win.__featurebaseBootedTheme === theme
      ) {
        return;
      }
      win.Featurebase("boot", {
        appId,
        email,
        userId,
        theme,
        language,
      });
      win.__featurebaseBooted = true;
      win.__featurebaseBootedEmail = email;
      win.__featurebaseBootedUserId = userId;
      win.__featurebaseBootedTheme = theme;
    });
  }, [appId, email, userId, theme, language]);
}

export default useFeaturebase;


